import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  formatCurrency,
  formatDate,
  formatDateTime,
} from "../../utils/formatters";
import {
  getSubscriptionDetail,
  type SubscriptionDetailCharge,
  type SubscriptionDetailHistoryEntry,
  type SubscriptionDetailResult,
  type SubscriptionDetailSnapshot,
} from "../../services/subscriptionDetail.service";

/**
 * Contact page "Subscription detail" (migration 1670): live, read-only Product & Charges and Change History from
 * Zuora for ONE subscription card. Collapsed by default -- nothing is fetched until an agent opens it. Rendered only for
 * the roles in utils/subscriptionDetailAccess (the server enforces the same list). Any Zuora problem shows a clear note
 * and leaves the subscription card above exactly as it is.
 */

const HISTORY_PREVIEW = 10;

const REASON_TEXT: Record<string, string> = {
  not_configured: "Zuora is not connected in this environment.",
  timeout: "Zuora did not answer in time.",
  error: "Zuora returned an error.",
  rate_limited: "Zuora asked us to slow down.",
  backoff: "Zuora asked us to slow down.",
  breaker: "Zuora has been failing, so lookups are paused for a few minutes.",
  in_flight:
    "Someone is loading this subscription right now. Try again in a few seconds.",
  rate_limit_user_per_min:
    "You have opened many subscriptions in the last minute. Try again shortly.",
  rate_limit_user_per_hour:
    "You have opened many subscriptions in the last hour. Try again later.",
  rate_limit_global_per_min:
    "Many agents are loading subscriptions right now. Try again shortly.",
  db_error: "The CRM could not start the lookup.",
};

const money = (n: number | null, currency: string | null) => {
  if (n === null || n === undefined) {
    return "—";
  }
  try {
    return formatCurrency(n, currency || "USD");
  } catch {
    return n.toFixed(2);
  }
};
const d = (v: string | null) => (v ? formatDate(v) : "—");
const periodText = (c: SubscriptionDetailCharge) =>
  c.isOneTime
    ? "One-time"
    : c.billingPeriod
      ? c.billingPeriod.replace(/_/g, " ")
      : "—";

function priceCell(c: SubscriptionDetailCharge): string {
  if (c.isDiscount) {
    if (c.discountPercentage !== null) {
      return `−${c.discountPercentage}%`;
    }
    if (c.discountAmount !== null) {
      return `−${money(c.discountAmount, c.currency)}`;
    }
    return c.pricingSummary ?? "Discount";
  }
  if (c.price !== null) {
    return money(c.price, c.currency);
  }
  return c.pricingSummary ?? "—";
}

function ChargesTable({
  charges,
  truncated,
}: {
  charges: SubscriptionDetailCharge[];
  truncated: boolean;
}) {
  if (charges.length === 0) {
    return (
      <p className="text-xs text-gray-500 italic">
        Zuora lists no charges on this subscription.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-xs" data-testid="zsub-charges-table">
        <thead>
          <tr className="text-left text-gray-500 border-b border-gray-200">
            <th className="py-1.5 pr-3 font-medium">Product / rate plan</th>
            <th className="py-1.5 pr-3 font-medium">Charge</th>
            <th className="py-1.5 pr-3 font-medium">Type · model</th>
            <th className="py-1.5 pr-3 font-medium text-right">Price</th>
            <th className="py-1.5 pr-3 font-medium">Billing</th>
            <th className="py-1.5 pr-3 font-medium text-right">Qty</th>
            <th className="py-1.5 pr-3 font-medium text-right">Total</th>
            <th className="py-1.5 pr-3 font-medium text-right">MRR</th>
            <th className="py-1.5 pr-3 font-medium">Effective</th>
          </tr>
        </thead>
        <tbody>
          {charges.map((c, i) => (
            <tr
              key={`${c.chargeNumber ?? ""}-${i}`}
              className="border-b border-gray-100 align-top"
            >
              <td className="py-1.5 pr-3">
                <div className="text-gray-900">{c.product ?? "—"}</div>
                <div className="text-gray-500">
                  {c.ratePlan ?? ""}
                  {c.ratePlanChange === "Remove" && (
                    <span className="ml-1 text-red-700">(removed)</span>
                  )}
                </div>
              </td>
              <td className="py-1.5 pr-3">
                <div className="text-gray-900">
                  {c.charge ?? "—"}
                  {c.isDiscount && (
                    <span className="ml-1 px-1.5 py-0.5 rounded bg-green-100 text-green-800">
                      Discount
                    </span>
                  )}
                  {c.isOneTime && (
                    <span className="ml-1 px-1.5 py-0.5 rounded bg-blue-100 text-blue-800">
                      One-time
                    </span>
                  )}
                </div>
                {c.description && (
                  <div className="text-gray-500">{c.description}</div>
                )}
              </td>
              <td className="py-1.5 pr-3 text-gray-700">
                {[c.type, c.model].filter(Boolean).join(" · ") || "—"}
              </td>
              <td className="py-1.5 pr-3 text-right text-gray-900 whitespace-nowrap">
                {priceCell(c)}
              </td>
              <td className="py-1.5 pr-3 text-gray-700">{periodText(c)}</td>
              <td className="py-1.5 pr-3 text-right text-gray-700">
                {c.quantity ?? "—"}
                {c.uom ? ` ${c.uom}` : ""}
              </td>
              <td className="py-1.5 pr-3 text-right text-gray-900 whitespace-nowrap">
                {money(c.total, c.currency)}
              </td>
              <td className="py-1.5 pr-3 text-right text-gray-700 whitespace-nowrap">
                {money(c.mrr, c.currency)}
              </td>
              <td className="py-1.5 pr-3 text-gray-700 whitespace-nowrap">
                {d(c.effectiveStartDate)} – {d(c.effectiveEndDate)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {truncated && (
        <p className="text-xs text-gray-500 mt-1">
          Only the first 200 charges are shown.
        </p>
      )}
    </div>
  );
}

function HistoryTable({
  entries,
}: {
  entries: SubscriptionDetailHistoryEntry[];
}) {
  const [showAll, setShowAll] = useState(false);
  const shown = showAll ? entries : entries.slice(0, HISTORY_PREVIEW);
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-xs" data-testid="zsub-history-table">
        <thead>
          <tr className="text-left text-gray-500 border-b border-gray-200">
            <th className="py-1.5 pr-3 font-medium">Version</th>
            <th className="py-1.5 pr-3 font-medium">Order / amendment</th>
            <th className="py-1.5 pr-3 font-medium">What changed</th>
            <th className="py-1.5 pr-3 font-medium">Booked</th>
            <th className="py-1.5 pr-3 font-medium">Contract effective</th>
            <th className="py-1.5 pr-3 font-medium">Service activation</th>
            <th className="py-1.5 pr-3 font-medium">Customer acceptance</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((h, i) => (
            <tr
              key={`${h.ref ?? h.kind}-${i}`}
              className="border-b border-gray-100 align-top"
            >
              <td className="py-1.5 pr-3 text-gray-900">{h.version ?? "—"}</td>
              <td className="py-1.5 pr-3 text-gray-700 whitespace-nowrap">
                {h.ref ?? (h.kind === "created" ? "Created" : "—")}
                {h.status && <div className="text-gray-500">{h.status}</div>}
              </td>
              <td className="py-1.5 pr-3 text-gray-900">
                {h.details.length > 0
                  ? h.details.map((x, j) => <div key={j}>{x}</div>)
                  : h.types.join(", ") || "—"}
                {h.description && (
                  <div className="text-gray-500">{h.description}</div>
                )}
              </td>
              <td className="py-1.5 pr-3 text-gray-700 whitespace-nowrap">
                {d(h.bookingDate)}
              </td>
              <td className="py-1.5 pr-3 text-gray-700 whitespace-nowrap">
                {d(h.contractEffectiveDate)}
              </td>
              <td className="py-1.5 pr-3 text-gray-700 whitespace-nowrap">
                {d(h.serviceActivationDate)}
              </td>
              <td className="py-1.5 pr-3 text-gray-700 whitespace-nowrap">
                {d(h.customerAcceptanceDate)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {entries.length > HISTORY_PREVIEW && (
        <button
          type="button"
          className="mt-1 text-xs text-indigo-600 hover:text-indigo-800"
          onClick={() => setShowAll((v) => !v)}
          data-testid="zsub-history-show-all"
        >
          {showAll
            ? `Show the newest ${HISTORY_PREVIEW}`
            : `Show all ${entries.length} changes`}
        </button>
      )}
    </div>
  );
}

const HISTORY_NOTE: Record<string, string> = {
  orders_page_cap: "Only the newest changes are shown (Zuora has more).",
  amendment_step_cap: "Only the newest changes are shown (Zuora has more).",
  amendment_chain_broken:
    "Zuora could not say what created the older versions.",
  amendment_cycle:
    "Zuora returned a looping change history; older versions are not shown.",
  amendment_missing:
    "Zuora did not return the change that created an older version, so older versions are missing.",
  amendment_malformed:
    "Zuora returned an unreadable change record, so older versions are missing.",
  orders_missing_older_versions:
    "Zuora's order history does not reach the first version (changes made before Orders was turned on are not listed).",
  orders_unsupported: "Zuora has no order history for this subscription.",
};

/** Reasons a retry can change (Zuora slow/failing/throttled); structural ones (no Orders before a date, a cap) cannot. */
export function historyReasonIsTransient(reason: string | null): boolean {
  return !!reason && /(rate_limited|timeout|error)$/.test(reason);
}

/** Why the history is incomplete, in words; unknown reasons fall back to the failure class. Always names the reason. */
export function historyReasonText(reason: string | null): string {
  if (reason && HISTORY_NOTE[reason]) {
    return HISTORY_NOTE[reason];
  }
  if (reason && /rate_limited$/.test(reason)) {
    return "Zuora asked us to slow down before the whole history was loaded.";
  }
  if (reason && /timeout$/.test(reason)) {
    return "Zuora did not answer in time for the whole history.";
  }
  if (reason && /error$/.test(reason)) {
    return "Zuora returned an error for part of the history.";
  }
  return "Part of the history could not be loaded.";
}

function Snapshot({ snap }: { snap: SubscriptionDetailSnapshot }) {
  const s = snap.detail.subscription;
  const h = snap.detail.history;
  const facts: [string, string][] = [
    ["Version", s.version !== null ? String(s.version) : "—"],
    ["Status", s.status ?? "—"],
    [
      "Term",
      s.termType === "EVERGREEN"
        ? "Evergreen"
        : `${d(s.termStartDate)} – ${d(s.termEndDate)}`,
    ],
    ["Auto-renew", s.autoRenew === null ? "—" : s.autoRenew ? "Yes" : "No"],
    ["Contracted MRR", money(s.contractedMrr, s.currency)],
    ["Total contract value", money(s.totalContractedValue, s.currency)],
  ];
  if (s.cancelledDate) {
    facts.splice(2, 0, ["Cancelled", d(s.cancelledDate)]);
  }
  return (
    <div className="space-y-4">
      <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 text-xs">
        {facts.map(([k, v]) => (
          <div key={k}>
            <dt className="text-gray-500">{k}</dt>
            <dd className="text-gray-900">{v}</dd>
          </div>
        ))}
      </dl>
      <div>
        <h4 className="text-xs font-semibold text-gray-700 mb-1">
          Product &amp; charges
        </h4>
        <ChargesTable
          charges={snap.detail.charges}
          truncated={snap.detail.chargesTruncated}
        />
      </div>
      <div>
        <h4 className="text-xs font-semibold text-gray-700 mb-1">
          Change history (newest first)
        </h4>
        {h.status === "unavailable" ? (
          <p
            className="text-xs text-amber-700"
            data-testid="zsub-history-unavailable"
          >
            Change history not available: {historyReasonText(h.reason)}
            {historyReasonIsTransient(h.reason)
              ? " Use Refresh to try again."
              : ""}
          </p>
        ) : (
          <>
            <HistoryTable entries={h.entries} />
            {h.status === "partial" && (
              <p className="text-xs text-amber-700 mt-1">
                <span data-testid="zsub-history-partial">
                  Incomplete history: {historyReasonText(h.reason)}
                  {historyReasonIsTransient(h.reason)
                    ? " Use Refresh to try again."
                    : ""}
                </span>
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const SubscriptionDetailPanel: React.FC<{ contactSubscriptionId: string }> = ({
  contactSubscriptionId,
}) => {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const key = ["zuora-subscription-detail", contactSubscriptionId];
  const q = useQuery({
    queryKey: key,
    queryFn: () => getSubscriptionDetail(contactSubscriptionId, false),
    enabled: open,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });
  const refresh = useMutation({
    mutationFn: () => getSubscriptionDetail(contactSubscriptionId, true),
    onSuccess: (r: SubscriptionDetailResult) => qc.setQueryData(key, r),
  });
  const r = q.data;
  if (r?.status === "forbidden") {
    return null;
  }
  const busy = q.isFetching || refresh.isPending;
  const snap: SubscriptionDetailSnapshot | null =
    r?.status === "ok" ? r : r?.status === "unavailable" ? r.stale : null;

  return (
    <div
      className="mt-3 pt-3 border-t border-gray-200"
      data-testid="zsub-detail"
    >
      <button
        type="button"
        className="text-xs font-medium text-indigo-600 hover:text-indigo-800"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        data-testid="zsub-detail-toggle"
      >
        {open ? "▾" : "▸"} Subscription detail from Zuora (charges and change
        history)
      </button>
      {open && (
        <div className="mt-3 space-y-3" data-testid="zsub-detail-body">
          <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
            {snap && (
              <span data-testid="zsub-fetched-at">
                {r?.status === "unavailable"
                  ? "Last answer from Zuora, fetched "
                  : "Fetched from Zuora "}
                {formatDateTime(snap.fetchedAt)}
              </span>
            )}
            <button
              type="button"
              className="px-2 py-0.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50"
              onClick={() => refresh.mutate()}
              disabled={busy}
              data-testid="zsub-refresh"
            >
              {busy ? "Loading…" : "Refresh"}
            </button>
            {snap?.zuoraUrl && (
              <a
                href={snap.zuoraUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-600 hover:text-indigo-800"
                data-testid="zsub-zuora-link"
              >
                Open in Zuora ↗
              </a>
            )}
          </div>

          {q.isLoading && (
            <p className="text-xs text-gray-500">
              Asking Zuora… (a few seconds)
            </p>
          )}
          {(q.isError || refresh.isError) && (
            <p className="text-xs text-red-700" data-testid="zsub-error">
              Subscription detail could not be loaded. The card above is
              unchanged.
            </p>
          )}
          {r?.status === "unavailable" && (
            <div
              className="text-xs rounded border border-amber-200 bg-amber-50 text-amber-800 px-3 py-2"
              data-testid="zsub-unavailable"
            >
              Zuora unavailable:{" "}
              {REASON_TEXT[r.reason] ?? "Zuora could not be reached."} The card
              above is unchanged.
              {r.stale ? " Showing the last answer we have." : ""}
            </div>
          )}
          {r?.status === "disabled" && (
            <p className="text-xs text-gray-500" data-testid="zsub-disabled">
              Subscription detail is turned off.
            </p>
          )}
          {r?.status === "not_in_zuora" && (
            <p className="text-xs text-gray-500">
              This record has no Zuora subscription to look up.
            </p>
          )}
          {r?.status === "not_found_in_zuora" && (
            <p className="text-xs text-gray-500" data-testid="zsub-not-found">
              Zuora has no subscription with this number.
            </p>
          )}
          {snap && <Snapshot snap={snap} />}
        </div>
      )}
    </div>
  );
};

export default SubscriptionDetailPanel;
